/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * meetingDraftSummary.ts — meetin\1 \2raf\1 \2ummary (services/operator-web-app).
 */

/**
 * E5-S3: Cross-widget consistency — meeting location <-> address state.
 * Plan: arm-operator/15 (E5-S3), wireframes 04–06.
 */
import type { WorkspaceState } from '@/store/slices/workspaceSlice';

const MEETING_FIELD_IDS = ['date', 'time', 'address'] as const;

export interface MeetingDraftSummary {
  date: string;
  time: string;
  address: string;
  /** True when all three (date, time, address) have non-empty trimmed values. */
  isComplete: boolean;
  /** Which of the three are filled (for display). */
  filled: readonly string[];
  missing: readonly string[];
}

/**
 * Returns a consistent summary of the meeting draft from workspace scenario fields.
 * Used by MeetingSummary component and for consistency tests.
 */
export function getMeetingDraftSummary(state: WorkspaceState): MeetingDraftSummary {
  const fields = state.scenarioFields;
  const date = (fields.date?.value ?? '').trim();
  const time = (fields.time?.value ?? '').trim();
  const address = (fields.address?.value ?? '').trim();

  const filled: string[] = [];
  const missing: string[] = [];
  for (const id of MEETING_FIELD_IDS) {
    const v = (fields[id]?.value ?? '').trim();
    if (v) filled.push(id);
    else missing.push(id);
  }

  return {
    date,
    time,
    address,
    isComplete: filled.length === MEETING_FIELD_IDS.length,
    filled,
    missing,
  };
}

/**
 * Formats a single line for display: "Дата в Время · Адрес" with "—" for empty parts.
 */
export function formatMeetingSummaryLine(summary: MeetingDraftSummary, atConnector = 'в'): string {
  const d = summary.date || '—';
  const t = summary.time || '—';
  const a = summary.address || '—';
  return `${d} ${atConnector} ${t} · ${a}`;
}
